/**
 * Contest finalise-readiness (admin-ui.md Overview tab; GL-16). The backend exposes no
 * precomputed readiness booleans — this derives the checklist and a finalise-guard preview
 * client-side from the AdminContest payload plus a live catalogue read of the curated markets.
 */
import { isHalfPointLine } from '@cou/over-under-engine';

/** The GL-16/GL-10 half-point-line rule, shared with the server's guard (no local re-derivation). */
export const isHalfPoint = isHalfPointLine;

export interface ReadinessContest {
  readonly campaignId: string;
  readonly round: string;
  readonly curation: {
    readonly eventIds: readonly string[];
    readonly marketIds: readonly string[];
  } | null;
  readonly payoutMatrix: Record<string, unknown>;
}

export interface CuratedMarketLine {
  readonly id: string;
  readonly name: string;
  readonly line: number;
  readonly verdict: 'selectable' | 'suspended' | 'out_of_bounds' | 'started';
}

export type ReadinessKey =
  'campaignId' | 'round' | 'slateCurated' | 'halfPointLines' | 'payoutMatrixComplete';

export interface ReadinessItem {
  readonly key: ReadinessKey;
  readonly label: string;
  readonly done: boolean;
  readonly hint?: string;
}

export interface ReadinessLoading {
  /** True while the live-catalogue read for the curated markets is still in flight. */
  readonly markets: boolean;
  /** True while the segment registry (config) read is still in flight. */
  readonly segments: boolean;
}

const NOT_LOADING: ReadinessLoading = { markets: false, segments: false };

/**
 * The 5-item finalise-readiness checklist (admin-ui.md Overview tab). `curatedMarkets` and
 * `segmentKeys` come from separate live reads that resolve after this contest itself loads —
 * `loading` distinguishes "still fetching" from "fetched and genuinely empty", so a line/matrix
 * check never reads as satisfied before its data has actually arrived.
 */
export function computeReadiness(
  contest: ReadinessContest,
  curatedMarkets: readonly CuratedMarketLine[],
  segmentRegistry: Readonly<Record<string, string>>,
  loading: ReadinessLoading = NOT_LOADING,
): readonly ReadinessItem[] {
  const slateCurated = contest.curation !== null && contest.curation.marketIds.length > 0;
  // Only pending when there is actually something to check — an uncurated contest has no markets
  // to wait on, so it isn't held on the markets fetch that never fires for it.
  const marketsPending = loading.markets && slateCurated;
  const invalidLines = curatedMarkets.filter((market) => !isHalfPoint(market.line));
  const allSegments = Array.from(new Set(['default', ...Object.keys(segmentRegistry)]));
  const emptySegments = allSegments.filter((segment) => !hasAnyTier(contest.payoutMatrix, segment));
  const emptySegmentNames = emptySegments.map((segment) =>
    segment === 'default' ? 'Default' : (segmentRegistry[segment] ?? segment),
  );

  return [
    {
      key: 'campaignId',
      label: 'Campaign ID set',
      done: contest.campaignId !== '',
      hint: contest.campaignId || undefined,
    },
    {
      key: 'round',
      label: 'Round set',
      done: contest.round !== '',
      hint: contest.round || undefined,
    },
    {
      key: 'slateCurated',
      label: 'Slate curated',
      done: slateCurated,
      hint:
        contest.curation !== null
          ? `${contest.curation.marketIds.length} props across ${contest.curation.eventIds.length} events`
          : undefined,
    },
    {
      key: 'halfPointLines',
      label: 'Every line half-point',
      done: slateCurated && !marketsPending && invalidLines.length === 0,
      hint: marketsPending
        ? 'Checking curated Markets…'
        : invalidLines.length > 0
          ? `${invalidLines.map((m) => `${m.name} sits at ${m.line.toFixed(1)}`).join(', ')}`
          : 'All lines are half-point',
    },
    {
      key: 'payoutMatrixComplete',
      label: 'Payout matrix complete',
      done: !loading.segments && emptySegments.length === 0,
      hint: loading.segments
        ? 'Checking the payout matrix…'
        : emptySegmentNames.length > 0
          ? `${formatSegmentList(emptySegmentNames)} segment${emptySegmentNames.length > 1 ? 's' : ''} have no tiers set`
          : 'All segments have tiers',
    },
  ];
}

function formatSegmentList(names: readonly string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

function hasAnyTier(payoutMatrix: Record<string, unknown>, segmentKey: string): boolean {
  const tiers = payoutMatrix[segmentKey];
  return typeof tiers === 'object' && tiers !== null && Object.keys(tiers).length > 0;
}

export interface FinaliseBlockingEntry {
  readonly marketId: string;
  readonly name: string;
  readonly line: string;
}

export interface FinaliseWarningEntry {
  readonly marketId: string;
  readonly name: string;
}

export interface FinaliseGuardPreview {
  /** Half-point-line violations (GL-16): never overridable. */
  readonly blocking: readonly FinaliseBlockingEntry[];
  /** Currently-suspended curated props (GL-16): overridable via `overrideSuspendedProps`. */
  readonly warnings: readonly FinaliseWarningEntry[];
}

/**
 * A client-side preview of the GL-16 finalise guards, computed from the same live catalogue read
 * used for the readiness checklist. The actual finalise call still reconciles against a live 409
 * (catalogue state can move between render and click) — this only lets the modal show named
 * props up front instead of waiting for the round trip.
 */
export function previewFinaliseGuards(
  curatedMarkets: readonly CuratedMarketLine[],
): FinaliseGuardPreview {
  return {
    blocking: curatedMarkets
      .filter((market) => !isHalfPoint(market.line))
      .map((market) => ({
        marketId: market.id,
        name: market.name,
        line: market.line.toFixed(1),
      })),
    warnings: curatedMarkets
      .filter((market) => market.verdict === 'suspended')
      .map((market) => ({
        marketId: market.id,
        name: market.name,
      })),
  };
}
